import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Edit, Trash2, Plus, GripVertical, Calendar, Circle } from 'lucide-react';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { ICON_MAP, ICON_OPTIONS, BADGE_COLORS, BULLET_OPTIONS } from './visualizationConstants';
import { BulletShape } from './BulletShape';
import DescriptionEditor from './DescriptionEditor';

const getContrastColor = (bg) => {
  if (!bg) return null;
  const hex = bg.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5 ? '#ffffff' : '#000000';
};

// Calculate spine left position for children container
// Container has pl-4 (16px). Spine must bisect child icon centers.
const getChildSpineLeft = (parentLevel, displayStyle) => {
  const childLevel = Math.min((parentLevel || 1) + 1, 5);
  if (displayStyle === 'bullets') {
    // Bullet centers: lv2=7px, lv3=6px, lv4=5px, lv5=5px
    const center = { 2: 7, 3: 6, 4: 5, 5: 5 }[childLevel] || 7;
    return 16 + center - 1; // 16 = pl-4
  }
  // Icon centers: lv2=16px(w-8), lv3=14px(w-7), lv4=12px(w-6), lv5=10px(w-5)
  const center = { 2: 16, 3: 14, 4: 12, 5: 10 }[childLevel] || 16;
  return 16 + center - 1; // 16 = pl-4
};

// Timeline Item Component
const TimelineItem = ({ 
  item, 
  depth = 0, 
  editable, 
  onEdit, 
  onDelete,
  onAddChild,
  primaryColor,
  secondaryColor,
  textColor,
  dateColor,
  displayStyle,
  lineConfig,
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [expanded, setExpanded] = useState(true);
  
  const IconComponent = ICON_MAP[item.icon] || Circle;
  const hasChildren = item.children && item.children.length > 0;
  const badgeColorClass = item.badge?.color ? BADGE_COLORS[item.badge.color] : BADGE_COLORS.blue;
  
  // Size based on level
  const iconSize = {
    1: 'w-10 h-10',
    2: 'w-8 h-8',
    3: 'w-7 h-7',
    4: 'w-6 h-6',
    5: 'w-5 h-5',
  }[item.level] || 'w-8 h-8';
  
  const titleSize = {
    1: 'text-lg font-semibold',
    2: 'text-base font-medium',
    3: 'text-sm font-medium',
    4: 'text-sm',
    5: 'text-xs',
  }[item.level] || 'text-base';
  
  return (
    <div className="relative">
      <div className={cn(
        "relative flex items-start gap-4 py-3 group",
        editable && "hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg px-2 -mx-2"
      )}>
        {/* Timeline dot */}
        <div className="flex flex-col items-center relative z-10">
          {displayStyle === 'bullets' ? (
            <BulletShape
              bulletStyle={item.bullet_style || 'dot'}
              color={primaryColor || '#8b5cf6'}
              level={item.level}
            />
          ) : (
            <div 
              className={cn(
                "rounded-full flex items-center justify-center shadow-sm",
                iconSize
              )}
              style={{ 
                backgroundColor: primaryColor || '#8b5cf6',
                color: 'white'
              }}
            >
              <IconComponent className={cn(
                item.level === 1 ? 'w-5 h-5' : 
                item.level === 2 ? 'w-4 h-4' :
                'w-3 h-3'
              )} />
            </div>
          )}
          
          {/* Connector to children */}
          {hasChildren && expanded && (() => {
            const childLc = (lineConfig || []).find(c => c.level === (depth + 2));
            if (!childLc?.enabled) return null;
            return (
              <div 
                className="w-0.5 flex-1 mt-1"
                style={{ backgroundColor: childLc.style === 'dashed' ? 'transparent' : (secondaryColor || '#e5e7eb'), borderLeft: childLc.style === 'dashed' ? `2px dashed ${secondaryColor || '#e5e7eb'}` : 'none' }}
              />
            );
          })()}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              {/* Date */}
              {item.date_display && (
                <p className={cn("text-xs mb-1", !dateColor && colors.textMuted, "group-hover:!text-black")} style={dateColor ? { color: dateColor } : undefined}>
                  {item.date_display}
                </p>
              )}
              
              {/* Title */}
              <div className="flex items-center gap-2">
                {hasChildren && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className={cn("p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700", colors.textMuted)}
                  >
                    {expanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}
                <h4 className={cn(titleSize, !textColor && colors.textPrimary, "group-hover:!text-black")} style={textColor ? { color: textColor } : undefined}>
                  {item.title}
                </h4>
                {item.badge && (
                  <span className={cn("text-xs px-2 py-0.5 rounded-full", badgeColorClass)}>
                    {item.badge.text}
                  </span>
                )}
                {item.is_user_edited && (
                  <span className="text-xs text-purple-600 dark:text-purple-400">
                    (edited)
                  </span>
                )}
              </div>
              
              {/* Description */}
              {item.description && (
                <div
                  className={cn(
                    "mt-1 prose prose-sm max-w-none dark:prose-invert prose-p:my-0.5 prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0",
                    item.level <= 2 ? "[&>*]:text-sm" : "[&>*]:text-xs",
                    !textColor && colors.textSecondary,
                    "group-hover:!text-black group-hover:[&_*]:!text-black"
                  )}
                  style={textColor ? { color: textColor, opacity: 0.8 } : undefined}
                  dangerouslySetInnerHTML={{ __html: item.description }}
                />
              )}
              
              {/* Source indicator */}
              {item.source?.entity_type && (
                <p className={cn("text-xs mt-1 italic", colors.textMuted, "group-hover:!text-black")}>
                  Source: {item.source.entity_type}
                </p>
              )}
            </div>
            
            {/* Actions */}
            {editable && (
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onEdit(item)}
                >
                  <Edit className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onAddChild(item)}
                >
                  <Plus className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7 text-red-500 hover:text-red-600"
                  onClick={() => onDelete(item)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Children */}
      {hasChildren && expanded && (
        <div className="ml-7 pl-4 relative">
          {/* Spine line — bisects child icon centers, per-level config */}
          {(() => {
            const childLc = (lineConfig || []).find(c => c.level === (depth + 2));
            if (!childLc?.enabled) return null;
            return (
              <div 
                className="absolute top-0 bottom-0 pointer-events-none"
                style={{
                  left: getChildSpineLeft(item.level, displayStyle),
                  borderLeft: `2px ${childLc.style} ${secondaryColor || '#e5e7eb'}`,
                }}
              />
            );
          })()}
          {item.children.map((child, index) => (
            <TimelineItem
              key={child.item_id || index}
              item={child}
              depth={depth + 1}
              editable={editable}
              onEdit={onEdit}
              onDelete={onDelete}
              onAddChild={onAddChild}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              textColor={textColor}
              dateColor={dateColor}
              displayStyle={displayStyle}
              lineConfig={lineConfig}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// Edit Modal
const EditItemModal = ({ item, onSave, onClose, displayStyle }) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const isBullets = displayStyle === 'bullets';
  const [formData, setFormData] = useState({
    title: item?.title || '',
    description: item?.description || '',
    icon: item?.icon || 'circle',
    bullet_style: item?.bullet_style || 'dot',
    date_display: item?.date_display || '',
    badge_text: item?.badge?.text || '',
    badge_color: item?.badge?.color || 'blue',
    level: item?.level || 1,
  });
  
  const handleSave = () => {
    onSave({
      ...item,
      ...formData,
      badge: formData.badge_text ? { text: formData.badge_text, color: formData.badge_color } : null,
      is_user_edited: true,
    });
    onClose();
  };
  
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-[874px]">
        <DialogHeader>
          <DialogTitle>Edit Timeline Item</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Enter title..."
            />
          </div>
          
          <div className="space-y-2">
            <Label>Description</Label>
            <DescriptionEditor
              content={formData.description}
              onChange={(html) => setFormData(prev => ({ ...prev, description: html }))}
              placeholder="Enter description..."
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{isBullets ? 'Bullet Style' : 'Icon'}</Label>
              {isBullets ? (
                <Select
                  value={formData.bullet_style}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, bullet_style: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BULLET_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <span className="w-4 text-center text-base">{opt.preview}</span>
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Select 
                  value={formData.icon} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, icon: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ICON_OPTIONS.map(icon => {
                      const Icon = ICON_MAP[icon];
                      return (
                        <SelectItem key={icon} value={icon}>
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {icon}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Level (1-5)</Label>
              <Select 
                value={String(formData.level)} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, level: parseInt(v) }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(level => (
                    <SelectItem key={level} value={String(level)}>
                      Level {level}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Date Display</Label>
            <Input
              value={formData.date_display}
              onChange={(e) => setFormData(prev => ({ ...prev, date_display: e.target.value }))}
              placeholder="e.g., Q1 2025"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Badge Text</Label>
              <Input
                value={formData.badge_text}
                onChange={(e) => setFormData(prev => ({ ...prev, badge_text: e.target.value }))}
                placeholder="e.g., In Progress"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Badge Color</Label>
              <Select 
                value={formData.badge_color} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, badge_color: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.keys(BADGE_COLORS).map(color => (
                    <SelectItem key={color} value={color}>
                      <div className="flex items-center gap-2">
                        <div className={cn("w-3 h-3 rounded", BADGE_COLORS[color])} />
                        {color}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Timeline Renderer
const TimelineRenderer = ({ 
  items, 
  config, 
  branding, 
  brandingOverrides,
  editable,
  onUpdate 
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const [editingItem, setEditingItem] = useState(null);
  
  // Merge branding
  const finalBranding = { ...branding, ...brandingOverrides };
  const primaryColor = finalBranding.primary_color || '#8b5cf6';
  const secondaryColor = finalBranding.secondary_color || '#c4b5fd';
  const textColor = finalBranding.text_color || null;
  const dateColor = finalBranding.date_color || null;
  const backgroundColor = finalBranding.background_color || null;
  const displayStyle = finalBranding.display_style || 'icons';
  const lineConfig = finalBranding.line_config || [
    { level: 1, enabled: true, style: 'dashed' },
    { level: 2, enabled: true, style: 'dashed' },
    { level: 3, enabled: true, style: 'dashed' },
  ];
  const logoPosition = finalBranding.logo_position || 'left';
  
  // Generate unique ID
  const generateId = () => `ti_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Handle edit
  const handleEdit = (item) => {
    setEditingItem(item);
  };
  
  // Handle save edit
  const handleSaveEdit = (updatedItem) => {
    const updateItemRecursive = (items) => {
      return items.map(i => {
        if (i.item_id === updatedItem.item_id) {
          return { ...i, ...updatedItem };
        }
        if (i.children) {
          return { ...i, children: updateItemRecursive(i.children) };
        }
        return i;
      });
    };
    
    onUpdate(updateItemRecursive(items));
  };
  
  // Handle delete
  const handleDelete = (item) => {
    const deleteItemRecursive = (items) => {
      return items
        .filter(i => i.item_id !== item.item_id)
        .map(i => ({
          ...i,
          children: i.children ? deleteItemRecursive(i.children) : undefined,
        }));
    };
    
    onUpdate(deleteItemRecursive(items));
  };
  
  // Handle add child
  const handleAddChild = (parentItem) => {
    const newChild = {
      item_id: generateId(),
      level: Math.min((parentItem.level || 1) + 1, 5),
      title: 'New Item',
      description: '',
      icon: 'circle',
      children: [],
    };
    
    const addChildRecursive = (items) => {
      return items.map(i => {
        if (i.item_id === parentItem.item_id) {
          return { ...i, children: [...(i.children || []), newChild] };
        }
        if (i.children) {
          return { ...i, children: addChildRecursive(i.children) };
        }
        return i;
      });
    };
    
    onUpdate(addChildRecursive(items));
    setEditingItem(newChild);
  };
  
  // Handle add root item
  const handleAddRootItem = () => {
    const newItem = {
      item_id: generateId(),
      level: 1,
      title: 'New Item',
      description: '',
      icon: 'circle',
      children: [],
    };
    
    onUpdate([...items, newItem]);
    setEditingItem(newItem);
  };
  
  const isVertical = config?.style !== 'horizontal';
  
  if (items.length === 0) {
    return (
      <div className={cn(
        "flex flex-col items-center justify-center h-64 rounded-lg border-2 border-dashed",
        colors.borderPrimary
      )}>
        <div className={cn("text-center", colors.textMuted)}>
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm font-medium mb-1">No timeline items yet</p>
          <p className="text-xs mb-4">Use the AI co-pilot to generate content from your products</p>
          {editable && (
            <Button onClick={handleAddRootItem} size="sm" className="gap-1">
              <Plus className="w-4 h-4" />
              Add Item Manually
            </Button>
          )}
        </div>
      </div>
    );
  }
  
  return (
    <div className="relative" style={backgroundColor ? { backgroundColor, borderRadius: '0.5rem', padding: '1rem' } : undefined}>
      {/* Header with branding */}
      {finalBranding.logo_url && (
        <div className={cn(
          "mb-6 flex items-center gap-3",
          logoPosition === 'center' ? 'justify-center' : logoPosition === 'right' ? 'justify-end' : 'justify-start'
        )}>
          <img 
            src={finalBranding.logo_url} 
            alt="Logo" 
            className="h-[80px] w-auto object-contain"
          />
        </div>
      )}
      
      {/* Timeline */}
      {isVertical ? (
        <div className="relative space-y-1">
          {/* Timeline spine — connects root-level items */}
          {(() => { const lc1 = lineConfig.find(c => c.level === 1); return lc1?.enabled && items.length > 1 ? (
            <div 
              className="absolute top-0 bottom-0 pointer-events-none"
              style={{
                left: displayStyle === 'bullets' ? 7 : 19,
                borderLeft: `2px ${lc1.style} ${secondaryColor}`,
              }}
            />
          ) : null; })()}
          {items.map((item, index) => (
            <TimelineItem
              key={item.item_id || index}
              item={item}
              editable={editable}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAddChild={handleAddChild}
              primaryColor={primaryColor}
              secondaryColor={secondaryColor}
              textColor={textColor}
              dateColor={dateColor}
              displayStyle={displayStyle}
              lineConfig={lineConfig}
            />
          ))}
        </div>
      ) : (
        // Horizontal stepper
        <div className="flex gap-4 overflow-x-auto pb-4">
          {items.map((item, index) => {
            const IconComponent = ICON_MAP[item.icon] || Circle;
            
            return (
              <div
                key={item.item_id || index}
                className={cn(
                  "flex-shrink-0 w-64 p-4 rounded-lg border transition-all",
                  editable && "hover:shadow-md",
                  !backgroundColor && colors.bgSecondary,
                  colors.borderPrimary
                )}
                style={backgroundColor ? { backgroundColor } : undefined}
              >
                {displayStyle === 'bullets' ? (
                  <div className="mb-3">
                    <BulletShape
                      bulletStyle={item.bullet_style || 'dot'}
                      color={primaryColor}
                      level={1}
                    />
                  </div>
                ) : (
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                )}
                {item.date_display && (
                  <p className={cn("text-xs mb-1", !dateColor && colors.textMuted)} style={dateColor ? { color: dateColor } : undefined}>
                    {item.date_display}
                  </p>
                )}
                <h4 className={cn("font-medium mb-1", !textColor && colors.textPrimary)} style={textColor ? { color: textColor } : undefined}>
                  {item.title}
                </h4>
                {item.description && (
                  <div
                    className={cn("text-sm prose prose-sm max-w-none dark:prose-invert prose-p:my-0.5", !textColor && colors.textSecondary)}
                    style={textColor ? { color: textColor, opacity: 0.8 } : undefined}
                    dangerouslySetInnerHTML={{ __html: item.description }}
                  />
                )}
                {item.badge && (
                  <span className={cn(
                    "inline-block mt-2 text-xs px-2 py-0.5 rounded-full",
                    BADGE_COLORS[item.badge.color]
                  )}>
                    {item.badge.text}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}
      
      {/* Add root item button */}
      {editable && (
        <div className="mt-6 pt-4 border-t border-dashed" style={{ borderColor: secondaryColor || 'var(--border)' }}>
          <Button
            variant="outline"
            onClick={handleAddRootItem}
            className="gap-1 w-full hover:!text-black"
            style={backgroundColor ? { color: getContrastColor(backgroundColor), borderColor: getContrastColor(backgroundColor) } : undefined}
          >
            <Plus className="w-4 h-4" />
            Add Item
          </Button>
        </div>
      )}
      
      {/* Edit Modal */}
      {editingItem && (
        <EditItemModal
          item={editingItem}
          onSave={handleSaveEdit}
          onClose={() => setEditingItem(null)}
          displayStyle={displayStyle}
        />
      )}
    </div>
  );
};

export default TimelineRenderer;
